'use client';

import React, {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDisclosure } from '@chakra-ui/react';

import { Dialog } from '../components/modal';
import { Button } from '../components/button';
import { Text } from '../components/text';

export interface ConfirmOptions {
  title?: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  isDestructive?: boolean;
}

interface ConfirmContextValue {
  confirm: (options?: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextValue | undefined>(undefined);

interface ConfirmHandle {
  open: (options: ConfirmOptions) => Promise<boolean>;
}

const DEFAULT_OPTIONS: Required<ConfirmOptions> = {
  title: 'Confirm Action',
  description: 'Are you sure you want to proceed?',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  isDestructive: false,
};

const ConfirmDialogShell = forwardRef<ConfirmHandle>(function ConfirmDialogShell(_, ref) {
  const disclosure = useDisclosure();
  const [options, setOptions] = useState<Required<ConfirmOptions>>(DEFAULT_OPTIONS);
  const promiseRef = useRef<((value: boolean) => void) | null>(null);

  const { onOpen, onClose } = disclosure;

  useImperativeHandle(
    ref,
    () => ({
      open: (next) =>
        new Promise<boolean>((resolve) => {
          promiseRef.current = resolve;
          setOptions({ ...DEFAULT_OPTIONS, ...next });
          onOpen();
        }),
    }),
    [onOpen]
  );

  const settle = useCallback(
    (result: boolean) => {
      promiseRef.current?.(result);
      promiseRef.current = null;
      onClose();
    },
    [onClose]
  );

  return (
    <Dialog.Root
      open={disclosure.open}
      onOpenChange={(e: { open: boolean }) => {
        if (!e.open) settle(false);
      }}
      role="alertdialog"
    >
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content>
          <Dialog.Header>
            {options.title}
            <Dialog.CloseTrigger />
          </Dialog.Header>
          <Dialog.Body>
            {options.description && (
              <Text color="#6B7280" marginBottom={4}>
                {options.description}
              </Text>
            )}
          </Dialog.Body>
          <Dialog.Footer>
            <Button variant="outline" onClick={() => settle(false)}>
              {options.cancelText}
            </Button>
            <Button
              variant={options.isDestructive ? 'danger' : 'primary'}
              colorPalette={options.isDestructive ? 'red' : 'blue'}
              onClick={() => settle(true)}
            >
              {options.confirmText}
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
});

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const ref = useRef<ConfirmHandle>(null);

  const value = useMemo<ConfirmContextValue>(
    () => ({
      confirm: (options = {}) => ref.current?.open(options) ?? Promise.resolve(false),
    }),
    []
  );

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <ConfirmDialogShell ref={ref} />
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context.confirm;
}

export function useStandaloneConfirm() {
  const ref = useRef<ConfirmHandle>(null);

  const confirm = useCallback(
    (options: ConfirmOptions = {}) => ref.current?.open(options) ?? Promise.resolve(false),
    []
  );

  const confirmDialog = useMemo(() => <ConfirmDialogShell ref={ref} />, []);

  return { confirm, confirmDialog };
}
